import { useState } from "react";
import { Button, Input, Modal, Typography, message } from "antd";
import { Link as LinkIcon, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useInviteAssessors } from "@hooks";

interface Props {
  termId: string;
  open: boolean;
  onClose: () => void;
  getContainer?: () => HTMLElement;
}

/// E-mails every assessor with assigned candidates a personal link to the proctoring wall (used on the wall and on term monitoring).
export const InviteAssessorsModal = ({ termId, open, onClose, getContainer }: Props) => {
  const { t } = useTranslation();
  const invite = useInviteAssessors(termId);
  const [note, setNote] = useState("");

  const send = () =>
    invite.mutate(
      { note: note.trim() || undefined },
      {
        onSuccess: ({ sent, failed }) => {
          if (sent.length > 0) message.success(t("proctoring.inviteSent", { count: sent.length }));
          if (failed.length > 0) message.warning(t("proctoring.inviteFailedList", { list: failed.join(", ") }));
          setNote("");
          onClose();
        },
        onError: (e: any) => message.error(typeof e?.response?.data === "string" ? e.response.data : t("proctoring.inviteFailed")),
      }
    );

  const copyWallLink = async () => {
    const realm = window.location.pathname.split("/")[1];
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${realm}/proctoring/${termId}`);
      message.success(t("proctoring.linkCopied"));
    } catch {
      message.error(t("proctoring.inviteFailed"));
    }
  };

  return (
    <Modal
      open={open}
      title={t("proctoring.inviteTitle")}
      okText={t("proctoring.inviteSend")}
      okButtonProps={{ icon: <Mail size={14} />, "data-testid": "invite-send" } as any}
      confirmLoading={invite.isPending}
      onOk={send}
      onCancel={onClose}
      getContainer={getContainer}
    >
      <Typography.Paragraph>{t("proctoring.inviteText")}</Typography.Paragraph>
      <Input.TextArea rows={3} maxLength={500} placeholder={t("proctoring.inviteNote")} value={note} onChange={(e) => setNote(e.target.value)} />
      <Button icon={<LinkIcon size={14} />} onClick={copyWallLink} style={{ marginTop: 12 }} data-testid="copy-wall-link">
        {t("proctoring.copyLink")}
      </Button>
    </Modal>
  );
};
